/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CButton,
  CSpinner,
  CAlert,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const MaterialInputs = ({ 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  submitResponse = null,
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    materialName: '',
    materialDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        materialName: initialData.MaterialName || '',
        materialDescription: initialData.MaterialDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            materialName: '',
            materialDescription: ''
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.materialName.trim()) {
      newErrors.materialName = t('Material Name Required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const submitData = {
          SessionUserId: String(userId),
          MaterialName: formData.materialName,
          MaterialDescription: formData.materialDescription
        };

        if (isEdit && initialData?.MaterialId) {
          submitData.MaterialId = initialData.MaterialId;
        }

        onSubmit(submitData);
      } else {
        setErrors({ general: t('userSessionNotFound') });
      }
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const getSuccessMessage = () => {
    if (isEdit) {
      return t('materialUpdated');
    }
    return t('materialCreated');
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="materialName">
              {t('Material Name')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="materialName"
              name="materialName"
              value={formData.materialName}
              onChange={handleInputChange}
              invalid={!!errors.materialName}
              disabled={isLoading}
              placeholder={t('enterMaterialName')}
            />
            {errors.materialName && (
              <div className="invalid-feedback d-block">
                {errors.materialName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="materialDescription">
              {t('Material Properties')}
            </CFormLabel>
            <CFormTextarea
              id="materialDescription"
              name="materialDescription"
              rows={4}
              value={formData.materialDescription}
              onChange={handleInputChange}
              invalid={!!errors.materialDescription}
              disabled={isLoading}
              placeholder={t('Enter Material Properties')}
            />
            {errors.materialDescription && (
              <div className="invalid-feedback d-block">
                {errors.materialDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {t('Saving')}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {t('createMaterial')}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('Cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

MaterialInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default MaterialInputs;
